import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, Check, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { getCallbackAvailability, submitCallback } from "@/lib/api";
import { SERVICES } from "@/lib/site";

const formatTime = (t) => {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${m.toString().padStart(2, "0")} ${period}`;
};

export default function CallbackDialog({ open, onOpenChange }) {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState();
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    service_needed: "Other",
    notes: "",
  });

  const dateStr = useMemo(() => (date ? format(date, "yyyy-MM-dd") : ""), [date]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(1);
        setDate(undefined);
        setSlots([]);
        setTime("");
        setSubmitted(false);
        setForm({ full_name: "", phone: "", email: "", service_needed: "Other", notes: "" });
      }, 300);
    }
  }, [open]);

  useEffect(() => {
    if (!dateStr) return;
    setSlotsLoading(true);
    setTime("");
    getCallbackAvailability(dateStr)
      .then((data) => {
        if (!data.open) {
          setSlots([]);
          toast.message("Closed on Sundays. Please pick another day.");
        } else {
          setSlots(data.slots || []);
        }
      })
      .catch(() => toast.error("Could not load availability."))
      .finally(() => setSlotsLoading(false));
  }, [dateStr]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please select a date and time first.");
      return;
    }
    if (!form.full_name || !form.phone) {
      toast.error("Please share your name and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await submitCallback({
        ...form,
        email: form.email || undefined,
        scheduled_date: dateStr,
        scheduled_time: time,
      });
      setSubmitted(true);
      toast.success("Callback scheduled! We'll be in touch.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="callback-dialog"
        className="max-w-2xl bg-white border-brand-line rounded-md p-0 max-h-[92vh] overflow-y-auto"
      >
        <div className="p-6 sm:p-8">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl md:text-3xl font-extrabold tracking-tighter text-brand-ink">
              {submitted ? "You're booked." : "Schedule Your Free Callback"}
            </DialogTitle>
            <DialogDescription className="text-brand-ash">
              {submitted
                ? "We'll call you at your selected time. Save our number 818-606-3769."
                : "Pick a day and time. We'll call to discuss your project — no obligation."}
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="mt-8 flex flex-col items-center text-center gap-4">
              <div className="w-14 h-14 rounded-full bg-brand-limestone flex items-center justify-center">
                <Check className="w-7 h-7 text-brand-obsidian" strokeWidth={2.5} />
              </div>
              <div>
                <p className="font-heading text-xl font-bold text-brand-ink">
                  {dateStr} · {formatTime(time)}
                </p>
                <p className="body-sm mt-1">A confirmation will be sent if you provided an email.</p>
              </div>
              <button onClick={() => onOpenChange(false)} className="btn-primary mt-2" data-testid="callback-done-btn">
                Done
              </button>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="overline mb-3">Step 1 · Pick a date</p>
                <div className="border border-brand-line rounded-md p-2 bg-white">
                  <Calendar
                    data-testid="callback-calendar"
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < today || d.getDay() === 0}
                    className="mx-auto"
                  />
                </div>
              </div>
              <div>
                <p className="overline mb-3">Step 2 · Pick a time</p>
                <div className="border border-brand-line rounded-md p-3 bg-brand-bone min-h-[180px]">
                  {!date ? (
                    <div className="flex items-center justify-center h-full py-12 text-brand-ash text-sm">
                      <CalendarClock className="w-5 h-5 mr-2" /> Select a date to see times
                    </div>
                  ) : slotsLoading ? (
                    <div className="flex items-center justify-center py-12 text-brand-ash text-sm">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading slots…
                    </div>
                  ) : slots.length === 0 ? (
                    <div className="text-center py-12 text-brand-ash text-sm">
                      No times available. Try another day.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s}
                          onClick={() => setTime(s)}
                          data-testid={`time-slot-${s}`}
                          className={`px-2 py-2 rounded-sm text-xs font-semibold border transition ${
                            time === s
                              ? "bg-brand-obsidian text-white border-brand-obsidian"
                              : "bg-white border-brand-line text-brand-ink hover:border-brand-obsidian"
                          }`}
                        >
                          {formatTime(s)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {date && time && (
                  <form onSubmit={handleSubmit} className="mt-5 space-y-3" data-testid="callback-form">
                    <p className="overline">Step 3 · Your details</p>
                    <input
                      data-testid="callback-name"
                      required
                      placeholder="Full name"
                      className="input-base"
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    />
                    <input
                      data-testid="callback-phone"
                      required
                      placeholder="Phone number"
                      className="input-base"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                    <input
                      data-testid="callback-email"
                      type="email"
                      placeholder="Email (optional)"
                      className="input-base"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                    <select
                      data-testid="callback-service"
                      className="input-base"
                      value={form.service_needed}
                      onChange={(e) => setForm({ ...form, service_needed: e.target.value })}
                    >
                      <option value="Other">Service needed…</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>{s.title}</option>
                      ))}
                    </select>
                    <textarea
                      data-testid="callback-notes"
                      rows={2}
                      placeholder="Quick note (optional)"
                      className="input-base resize-none"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                    <button
                      data-testid="callback-submit"
                      type="submit"
                      disabled={submitting}
                      className="btn-primary w-full"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Confirm Callback · {formatTime(time)}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
